import { CartAddEvent } from '@theme/events';

const normal = value => String(value || '').toLowerCase().replace(/['’]/g, '').replace(/\blogos?\b/g, '').replace(/[^a-z0-9]+/g, ' ').trim().replace(/^(nba|mlb|nhl)\s+/, '');
const titleStart = value => normal(value).replace(/^(mens?|womens?|unisex|youths?|kids?|childrens?|babys?)\s+/, '').replace(/^(nba|mlb|nhl)\s+/, '');
const blocked = value => /\bnfl\b|national football league/i.test(value);
const generic = /^(nba|mlb|nhl|headwear|apparel|brand|brands|sports?|logo|companies|clothing|accessories|new arrivals|sale|best sellers?|top sellers?|footwear|automotive|mens?|womens?|unisex|youths?|kids?|cotton|performance|polyester|long ?sleeves?|t ?shirts?|tee|hoodie|pullover|zipper|beanie|snapback|baseball|dad|hat|cap|shorts|joggers|sweatshirt|tank|drawstring|bag|romper|onesie|polo|quarter zip)(\s|$)/;
const make = (tag, className, text) => {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
};
const readTags = element => {
  try { return JSON.parse(element.dataset.tags || '[]'); } catch { return []; }
};
class ShopDesign extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.load().catch(() => { this.hidden = true; });
  }
  async load() {
    const tags = readTags(this);
    if (blocked(tags.join(' ') + ' ' + this.dataset.title)) return;
    const title = titleStart(this.dataset.title);
    const type = normal(this.dataset.type);
    const candidates = tags.map(tag => normal(tag)).filter(tag =>
      tag.length > 1 && tag !== type && !generic.test(tag) &&
      (title === tag || title.startsWith(tag + ' '))
    ).sort((a, b) => b.length - a.length);
    const design = candidates[0];
    if (!design) return;
    this.base = window.Shopify?.routes?.root || '/';
    this.currency = window.Shopify?.currency?.active || this.dataset.currency || 'USD';
    this.money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.currency
    }).format(cents / 100);
    const query = '"' + design + '"';
    const url = new URL(this.dataset.searchUrl, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[unavailable_products]', 'hide');
    const more = this.querySelector('.tc-design-more');
    more.href = url.pathname + url.search;
    const current = new URL(location.href);
    if (current.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', current.searchParams.get('preview_theme_id'));
    url.searchParams.set('section_id', 'tc-design-results');
    const response = await fetch(url, { signal: AbortSignal.timeout(15000) });
    if (!response.ok) return;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const matches = Array.from(doc.querySelectorAll('[data-tc-design-results] [data-handle]')).filter(item => {
      const itemTags = readTags(item);
      const start = titleStart(item.dataset.title);
      return item.dataset.productId !== this.dataset.productId &&
        !blocked(itemTags.join(' ') + ' ' + item.dataset.title) &&
        itemTags.some(tag => normal(tag) === design) &&
        (start === design || start.startsWith(design + ' '));
    });
    // Prefer different garment types, without mixing unrelated designs.
    matches.sort((a, b) => Number(normal(a.dataset.type) === type) - Number(normal(b.dataset.type) === type));
    const seenTypes = new Set();
    const diverse = [], remaining = [];
    matches.forEach(item => {
      const key = normal(item.dataset.type);
      if (seenTypes.has(key)) remaining.push(item);
      else { seenTypes.add(key); diverse.push(item); }
    });
    const selected = [...diverse, ...remaining].slice(0, 9);
    const products = await Promise.all(selected.map(async item => {
      try {
        const r = await fetch(this.base + 'products/' + encodeURIComponent(item.dataset.handle) + '.js', { signal: AbortSignal.timeout(12000) });
        if (!r.ok) return null;
        const p = await r.json();
        if (!p.available || !p.featured_image || !p.variants?.some(v => v.available)) return null;
        return p;
      } catch { return null; }
    }));
    if (!this.isConnected) return;
    const grid = this.querySelector('.tc-design-grid');
    products.filter(Boolean).slice(0, 6).forEach(p => grid.append(this.card(p)));
    if (!grid.children.length) return;
    this.hidden = false;
  }
  card(product) {
    const card = make('article', 'tc-design-card');
    const link = make('a', 'tc-design-image');
    link.href = this.base + 'products/' + product.handle;
    const image = make('img');
    image.width = 400; image.height = 400; image.loading = 'lazy';
    image.alt = product.title;
    link.append(image); card.append(link);
    const body = make('div', 'tc-design-body');
    const title = make('a', 'tc-design-title', product.title);
    title.href = link.href;
    const price = make('div', 'tc-design-price');
    const form = make('form', 'tc-design-form');
    form.method = 'post'; form.action = this.base + 'cart/add';
    const id = make('input'); id.type = 'hidden'; id.name = 'id';
    const button = make('button', '', 'Add to cart'); button.type = 'submit';
    const message = make('p', 'tc-design-message'); message.setAttribute('role', 'status');
    const available = product.variants.filter(v => v.available);
    let variant = available[0];
    const selectors = [];
    const options = product.options || [];
    if (!(options.length === 1 && options[0].name === 'Title' && options[0].values?.[0] === 'Default Title')) {
      options.forEach((option, index) => {
        const label = make('label', '', option.name);
        const select = make('select');
        select.setAttribute('aria-label', option.name + ' for ' + product.title);
        const values = [...new Set(product.variants.map(v => v.options[index]))];
        values.forEach(value => { const o = make('option', '', value); o.value = value; select.append(o); });
        select.value = variant.options[index];
        select.addEventListener('change', () => {
          const exact = available.find(v => selectors.every((s, i) => v.options[i] === s.value));
          variant = exact || available.find(v => v.options[index] === select.value);
          if (variant) selectors.forEach((s, i) => { s.value = variant.options[i]; });
          update();
        });
        selectors.push(select); label.append(select); form.append(label);
      });
    }
    const update = () => {
      selectors.forEach((select, index) => {
        Array.from(select.options).forEach(option => {
          // The first option remains freely selectable; later choices reflect preceding choices.
          option.disabled = !available.some(v => v.options[index] === option.value && selectors.slice(0, index).every((s, i) => v.options[i] === s.value));
        });
      });
      button.disabled = !variant;
      if (!variant) { id.value = ''; message.textContent = 'This combination is unavailable.'; return; }
      id.value = String(variant.id);
      price.textContent = this.money(variant.price);
      const imageSource = variant.featured_image?.src || product.featured_image;
      const imageUrl = new URL(imageSource, location.origin);
      imageUrl.searchParams.set('width', '400');
      image.src = imageUrl.href;
      link.href = this.base + 'products/' + product.handle + '?variant=' + variant.id;
      title.href = link.href;
      message.textContent = '';
      message.removeAttribute('data-error');
    };
    form.append(id, button, message);
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (!variant || button.disabled) return;
      const chosen = variant;
      button.disabled = true; button.textContent = 'Adding…';
      message.textContent = ''; message.removeAttribute('data-error');
      selectors.forEach(s => { s.disabled = true; });
      let added = false;
      try {
        const sectionIds = [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map(e => e.dataset.sectionId))].slice(0, 5);
        const response = await fetch(this.base + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: chosen.id, quantity: 1 }], sections: sectionIds, sections_url: location.pathname })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Could not add this item.');
        added = true;
        message.textContent = 'Added: ' + chosen.title;
        const cartLink = make('a', 'tc-design-cart', 'View cart');
        cartLink.href = this.dataset.cartUrl; message.append(cartLink);
        this.dispatchEvent(new CartAddEvent(undefined, 'tc-design-' + product.id, {
          source: 'product-form-component', itemCount: 1, productId: String(product.id), variantId: String(chosen.id), sections: result.sections
        }));
      } catch (error) {
        if (!added) {
          message.setAttribute('data-error', '');
          message.textContent = error instanceof TypeError ? 'Could not confirm the addition. Check your cart before trying again.' : error.message;
        }
      } finally {
        button.disabled = !variant; button.textContent = 'Add to cart';
        selectors.forEach(s => { s.disabled = false; });
      }
    });
    body.append(title, price, form); card.append(body); update();
    return card;
  }
}
if (!customElements.get('tc-shop-design')) customElements.define('tc-shop-design', ShopDesign);
